import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {findDOMNode} from 'react-dom';
import Calendar from '../Calendar/Calendar/Calendar';
import throttle from 'lodash.throttle';
import './NavCalendarScrolling.less';

class NavCalendarScrolling extends Component {
    static propTypes = {
        /** Flag that specifies whether year has to be displayed first e.g. ja-JP*/
        displayYearFirst: PropTypes.bool,
        /** Vertically scrolling calendar that can be embedded on desktop, e.g. in the ChatBot sidebar. */
        isInline: PropTypes.bool,
        /** Object containing translations for the current locale. It is assumed that the parent app will prepare and provide the i18n object matching the shape seen here. Required for a11y. */
        i18n: PropTypes.shape({
            ariaLabelCheckinCleared: PropTypes.string,
            ariaLabelCheckinSelected: PropTypes.string,
            ariaLabelCheckoutCleared: PropTypes.string,
            ariaLabelCheckoutSelected: PropTypes.string,
            ariaLabelDatesCleared: PropTypes.string,
            ariaLabelNotValidCheckin: PropTypes.string,
            ariaLabelNotValidCheckout: PropTypes.string,
            ariaLabelUnavailable: PropTypes.string
        }),
        /** Optional labels object to override default locale-driven labels */
        labels: PropTypes.shape({
            dayNames: PropTypes.arrayOf(PropTypes.string),
            monthNames: PropTypes.arrayOf(PropTypes.string),
            shortDayNames: PropTypes.arrayOf(PropTypes.string),
            shortMonthNames: PropTypes.arrayOf(PropTypes.string),
            /** To suppport locales that need a year label e.g. ja-JP*/
            yearLabel: PropTypes.string
        }),
        /** A moniker or BCP-47 style language tag string. Defaults to 'en-US'. */
        locale: PropTypes.string,
        /** Maximum allowable date after which the next button will be disabled; scrollable mode stops loading more months. */
        maxDate: PropTypes.instanceOf(Date),
        /** Minimum allowable date before which the prev button will be disabled; scrollable mode uses month and year props. */
        minDate: PropTypes.instanceOf(Date),
        /** Default 767, Point at which mobile vs desktop views are switched. Anything below or equal to this number will result in mobile view. */
        mobileBreakpoint: PropTypes.number,
        /** Zero-based index representing the month. This is only necessary if you want to force the calendar to a specific month. By default, displays the month of any selected date or range. If nothing is selected, defaults to today's month. */
        month: PropTypes.number,
        /** Initial number of months to show in scrolling calendar to ensure that a scroll overflow is created to enable infinite scroll. (Defaults to 4 for isInline)*/
        numMonthsToDisplay: PropTypes.number,
        /** onHoverDay(date) Triggered when hoverDate is changed.
         * Used to set endDate in alertTemplateData according to hoverDate  */
        onHoverDay: PropTypes.func,
        /** Turns on date range selection */
        selectable: PropTypes.bool,
        /** Scroll to the selected month on render */
        scrollToMonth: PropTypes.bool,
        /** By default, the calender elements are given a data-wdio attribute of "calendar-controls" and "calender".
        If you have more than one on a page you can add a prefix to those two strings by using this property. */
        wdioPrefix: PropTypes.string,
        /** Number representing the year, e.g. 2016, . This is only necessary if you want to force the calendar to a specific month. By default, displays the month of any selected date or range. If nothing is selected, defaults to today's month. */
        year: PropTypes.number
    };

    static defaultProps = {
        displayYearFirst: false,
        isInline: false,
        i18n: {
            ariaLabelCheckinCleared: 'Check-in date cleared.',
            ariaLabelCheckinSelected: '{0} is selected as your check-in date.',
            ariaLabelCheckoutCleared: 'Checkout date cleared.',
            ariaLabelCheckoutSelected: '{0} is selected as your checkout date.',
            ariaLabelDatesCleared: 'Dates cleared.',
            ariaLabelNotValidCheckin: '{0} is not a valid check in date.',
            ariaLabelNotValidCheckout: '{0} is not a valid checkout date.',
            ariaLabelUnavailable: '{0} is unavailable.'
        },
        labels: {
            yearLabel: ''
        },
        maxDate: null,
        minDate: new Date(),
        numMonthsToDisplay: 2
    };

    constructor(props) {
        super(props);

        const {month, year} = this.getMonthYear(props);

        // For isInline calendar, we need at least 4 months initially loaded to ensure
        // a scroll overflow is created to enable infinite scroll on large height viewports
        let inlineNumMonths = 4;
        if (props.isInline && props.numMonthsToDisplay > 4) {
            inlineNumMonths = props.numMonthsToDisplay;
        }

        // When mobile mode is "scrolling", it takes too long to render a lot of months up front.
        // Instead we are using an infinite scrolling technique. We start by rendering a single month,
        // then listen for scroll events and load additional months as we approach the end of the scroll
        // list. If there is a selection, we want to scroll to the month with the selection. In order for
        // that to work, we may need to render more than 1 month at the start. getInitialNumMonths() determines
        // how many months to load at the start.
        const loadedMonths = this.getInitialNumMonths(
            month,
            year,
            props.selectedDate,
            props.selectionStart,
            props.scrollToMonth,
            (props.isInline && inlineNumMonths) || props.numMonthsToDisplay
        );

        this.state = {
            month,
            year,
            loadedMonths
        };

        this.lazyLoadHandler = throttle(this.lazyLoadHandler.bind(this), 80);
        this.maxLoadableMonths = props.maxDate
            ? this.getMaxNumMonths(month, year, props.maxDate)
            : null;
    }

    componentDidMount() {
        this.setupInfiniteScrolling();
    }

    // eslint-disable-next-line complexity
    componentDidUpdate(prevProps) {
        const {month, year} = this.props;

        // If month changes and we have a year, update state.
        if (month !== prevProps.month && (year || prevProps.year)) {
            this.setState({
                month,
                year: year || prevProps.year
            });
        }

        // If year changes and we have a month, update state.
        if (year !== prevProps.year && (month >= 0 || prevProps.month >= 0)) {
            this.setState({
                month: month >= 0 ? month : prevProps.month,
                year
            });
        }
    }

    componentWillUnmount() {
        this.scrollParent.removeEventListener('scroll', this.lazyLoadHandler);
    }

    getInitialNumMonths(
        month,
        year,
        selectedDate,
        selectionStart,
        scrollToMonth,
        numMonths
    ) {
        const selected = selectedDate || selectionStart || '';

        if (selected && scrollToMonth) {
            const selectedMonth = selected.split('-')[1];
            const selectedYear = selected.split('-')[0];
            const selDate = new Date(selectedYear, selectedMonth, 1);
            const monthDate = new Date(year, month, 1);

            // In certain circumstances this can return a negative number.
            //  1) selection date is in the past
            //  2) consumer is hardwiring the month/year (if not, all is well)
            //  3) has to be in mobile view with mode to "scrolling"
            // It is not immediately obvious what to do in these situations, and since we
            // do not have a use case that fits the scenario above, we'll punt on it for now.
            return (
                selDate.getMonth() -
                monthDate.getMonth() +
                12 * (selDate.getFullYear() - monthDate.getFullYear()) +
                1
            );
        }

        return numMonths;
    }

    getMaxNumMonths(month, year, maxDate) {
        // Get the maximum number of months to load, including start and end months
        let numMonths;
        const monthDate = new Date(year, month, 1);
        numMonths = (maxDate.getFullYear() - monthDate.getFullYear()) * 12;
        numMonths -= monthDate.getMonth();
        numMonths += maxDate.getMonth() + 1;
        return numMonths <= 0 ? 0 : numMonths;
    }

    // Initialize what is needed for infinite scrolling.
    setupInfiniteScrolling() {
        try {
            this.elem = findDOMNode(this);

            // Determine what element (or window) scrolls
            this.scrollParent = this.getScrollParent(this.elem);
            if (this.elem === this.scrollParent) {
                this.elem = this.elem.firstChild;
            }

            // Listen for scroll events
            this.scrollParent.addEventListener('scroll', this.lazyLoadHandler);

            // Populate initially so that num months shown fill the height of the screen
            this.lazyLoadHandler();
        } catch (e) {
            // Used to catch errors during unit testing. Should not happen
            // in browser.
        }
    }

    // Utility to get the scrolling element. We can't guarantee that the window
    // is what is scrolling. The datepickers, for example, scroll within a div.
    getScrollParent(element) {
        let style = window.getComputedStyle(element);
        const excludeStaticParent = style.position === 'absolute';
        const overflowRegex = /(auto|scroll)/;

        if (style.position === 'fixed') {
            return window;
        }

        while (element) {
            style = window.getComputedStyle(element);
            if (excludeStaticParent && style.position === 'static') {
                continue;
            }
            if (overflowRegex.test(style.overflow + style.overflowY)) {
                return element;
            }
            element = element.parentElement;
        }

        return window;
    }

    // Throttled call that loads new months if we approach the end of the list
    lazyLoadHandler() {
        const {loadedMonths} = this.state;
        const elemBottom = this.elem.getBoundingClientRect().bottom;
        const viewPortHeight =
            this.scrollParent.clientHeight || this.scrollParent.innerHeight;
        const diffBuffer = viewPortHeight * 0.8;

        if (elemBottom - diffBuffer <= viewPortHeight) {
            if (
                loadedMonths < this.maxLoadableMonths ||
                this.maxLoadableMonths === null
            ) {
                this.setState({loadedMonths: loadedMonths + 1});
            }
        }
    }

    getMonthYear(props) {
        // If month and year were passed in, use those
        if ((props.month || props.month === 0) && props.year) {
            return {month: props.month, year: props.year};
        }

        // If there is a selection (range or single), use that
        const dateString = props.selectionStart || props.selectedDate;

        if (dateString) {
            const dateElems = dateString.split('-');
            return {month: +dateElems[1] - 1, year: +dateElems[0]};
        }

        // Otherwise init the calendar with today's month
        const today = new Date();
        return {month: today.getMonth(), year: today.getFullYear()};
    }

    // eslint-disable-next-line complexity
    render() {
        // Some of these are itemized to strip them out. ...rest is passed into calendar
        const {
            className,
            displayYearFirst,
            i18n,
            labels,
            locale,
            onHoverDay,
            wdioPrefix,
            ...rest
        } = this.props;

        const dataWdio = wdioPrefix
            ? `${wdioPrefix}-calendar-controls`
            : 'calendar-controls';

        const numColumns = 1;
        const numMonths = this.state.loadedMonths;

        return (
            <div
                className={`${className || ''} nav-calendar-scrolling__wrapper`}
                data-wdio={dataWdio}
            >
                <Calendar
                    {...rest}
                    activeMonth={this.state.activeMonth}
                    displayYearFirst={displayYearFirst}
                    i18n={i18n}
                    labels={labels}
                    locale={locale}
                    month={this.state.month}
                    numColumns={numColumns}
                    numMonthsToDisplay={numMonths}
                    onHoverDay={onHoverDay}
                    padWeeks
                    wdioPrefix={wdioPrefix}
                    year={this.state.year}
                />
            </div>
        );
    }
}

export default NavCalendarScrolling;
